import {
  Input,
  FormField,
  SpaceBetween,
  RadioGroup,
  Container,
  ColumnLayout,
  Checkbox,
  Header,
  Box,
} from "@cloudscape-design/components-themed";
import {
  CLASSIFICATION,
  LABELS,
  ORDER_TARGET,
  POS_REGISTRATION_CONDITION,
  EXAMINATION_STATUS,
  PLACE_HOLDER,
  EXAMINATION_TYPE,
} from "./constants";
import PeriodDatePicker from "../UI/date-picker/PeriodDatePicker";
import { COMMON_LABELS } from "../default-constants";
import { Dispatch, SetStateAction } from "react";
import { ExaminationStatusSearchRequest } from "../../features/api-interfaces";
import { formatDate } from "../../utils/format";

interface IDetailSearchTargetCondition {
  searchInput: ExaminationStatusSearchRequest;
  setSearchInput: Dispatch<SetStateAction<ExaminationStatusSearchRequest>>;
  changeMerchantApplicationCondition: () => void;
  applicationDisableFlg: boolean;
  posDisableFlg: boolean;
  docomoDisableFlg: boolean;
  checkSettlementGroupId: (settlementGroupId: string) => boolean;
  settlementGroupIdErrorMsg: string;
  checkAccessKey: (accessKey: string) => boolean;
  accessKeyErrorMsg: string;
  changeOrderTargetExceptingNttf: (inputValue: string) => void;
  changeOrderTarget: (inputValue: string) => void;
  vm: boolean;
  setVm: Dispatch<SetStateAction<boolean>>;
  changeOrderTargetVm: (inputValue: boolean) => void;
  changePosRegistrationCondition: () => void;
  changeDocomoRegistrationCondition: () => void;
}

/**
 * 詳細検索 確認状況エリア
 */
export const DetailSearchTargetCondition = (
  props: IDetailSearchTargetCondition
) => {
  const {
    searchInput,
    setSearchInput,
    changeMerchantApplicationCondition,
    applicationDisableFlg,
    posDisableFlg,
    docomoDisableFlg,
    checkSettlementGroupId,
    settlementGroupIdErrorMsg,
    checkAccessKey,
    accessKeyErrorMsg,
    changeOrderTargetExceptingNttf,
    changeOrderTarget,
    vm,
    setVm,
    changeOrderTargetVm,
    changePosRegistrationCondition,
    changeDocomoRegistrationCondition,
  } = props;

  /**
   * 日付の入力ハンドラ.
   * @param {string} startDate 開始日
   * @param {string} endDate 終了日
   * @param {boolean} startDateFlg 開始日か
   * @return {string} フォーマット済みの日付（yyyy/MM/dd形式）または空文字
   */
  const changeDate = (
    startDate: string,
    endDate: string,
    startDateFlg: boolean
  ): string => {
    const isEmptyDate = (date: string) => date === "";

    if (startDateFlg) {
      if (isEmptyDate(startDate) || isEmptyDate(endDate)) {
        return formatDate(startDate);
      }
      return startDate > endDate ? "" : formatDate(startDate);
    } else {
      if (isEmptyDate(startDate) || isEmptyDate(endDate)) {
        return formatDate(endDate);
      }
      return startDate > endDate ? "" : formatDate(endDate);
    }
  };

  return (
    <Container
      header={<Header variant={"h3"}>{LABELS.CHECK_TARGET_CONDITION}</Header>}
    >
      <SpaceBetween direction={"vertical"} size={"l"}>
        {/* 加盟店審査状況 */}
        <Container>
          <SpaceBetween direction={"vertical"} size={"l"}>
            <RadioGroup
              items={[
                {
                  value: "1",
                  label: LABELS.MERCHANT_APPLICATION_CONDITION,
                },
              ]}
              onChange={changeMerchantApplicationCondition}
              value={searchInput.checkTargetCode.toString()}
            />
            <SpaceBetween direction={"vertical"} size={"l"}>
              <ColumnLayout columns={5}>
                <PeriodDatePicker
                  label={LABELS.APPLICATION_DATE}
                  startDate={searchInput.examinationApplicationStartDate}
                  disabled={applicationDisableFlg}
                  startDateOnChange={({ detail: { value } }) =>
                    setSearchInput({
                      ...searchInput,
                      examinationApplicationStartDate: changeDate(
                        formatDate(value),
                        searchInput.examinationApplicationEndDate,
                        true
                      ),
                    })
                  }
                  endDate={searchInput.examinationApplicationEndDate}
                  endDateOnChange={({ detail: { value } }) =>
                    setSearchInput({
                      ...searchInput,
                      examinationApplicationEndDate: changeDate(
                        searchInput.examinationApplicationStartDate,
                        formatDate(value),
                        false
                      ),
                    })
                  }
                />
              </ColumnLayout>
              <ColumnLayout columns={4}>
                <FormField
                  label={LABELS.SETTLEMENT_GROUP_ID}
                  constraintText={PLACE_HOLDER.CHAR_LENGTH_TWENTY_SIX}
                  errorText={settlementGroupIdErrorMsg}
                >
                  <Input
                    value={searchInput.settlementGroupId}
                    onChange={({ detail }) => {
                      const inputValue = detail.value;
                      setSearchInput({
                        ...searchInput,
                        settlementGroupId: inputValue,
                      });
                      checkSettlementGroupId(inputValue);
                    }}
                  />
                </FormField>
                <FormField
                  label={LABELS.ACCESS_KEY}
                  constraintText={PLACE_HOLDER.CHAR_LENGTH_TWENTY_SIX}
                  errorText={accessKeyErrorMsg}
                >
                  <Input
                    value={searchInput.accessKey}
                    onChange={({ detail }) => {
                      const inputValue = detail.value;
                      setSearchInput({
                        ...searchInput,
                        accessKey: inputValue,
                      });
                      checkAccessKey(inputValue);
                    }}
                  />
                </FormField>
              </ColumnLayout>
              <ColumnLayout columns={7}>
                <FormField label={LABELS.ORDER_TARGET}>
                  <SpaceBetween direction={"horizontal"} size={"l"}>
                    <RadioGroup
                      items={[
                        {
                          value: "1",
                          label: ORDER_TARGET.ALL,
                          disabled: applicationDisableFlg,
                        },
                        {
                          value: "2",
                          label: ORDER_TARGET.NTT_D,
                          disabled: applicationDisableFlg,
                        },
                        {
                          value: "3",
                          label: ORDER_TARGET.NOT_NTT_D,
                          disabled: applicationDisableFlg,
                        },
                      ]}
                      onChange={({ detail: { value } }) => {
                        const isSelectedNonNTTF = value === "2";
                        setSearchInput({
                          ...searchInput,
                          acquirer: value,
                          smccFlg: isSelectedNonNTTF,
                          tfFlg: isSelectedNonNTTF,
                          saisonFlg: isSelectedNonNTTF,
                          jcbFlg: isSelectedNonNTTF,
                          amexFlg: isSelectedNonNTTF,
                        });
                        changeOrderTargetExceptingNttf(value);
                        changeOrderTarget(value);
                      }}
                      value={searchInput.acquirer}
                    />
                    <FormField label={""}>
                      <SpaceBetween direction={"vertical"} size={"xxxs"}>
                        <Box>{LABELS.BLANK}</Box>
                        <Box>{LABELS.BLANK}</Box>
                        <SpaceBetween direction={"horizontal"} size={"l"}>
                          <Checkbox
                            checked={vm}
                            onChange={({ detail: { checked } }) => {
                              setVm(checked);
                              setSearchInput({
                                ...searchInput,
                                smccFlg: checked,
                                tfFlg: checked,
                                saisonFlg: checked,
                              });
                              changeOrderTargetVm(checked);
                            }}
                            disabled={searchInput.acquirer !== "2"}
                          >
                            {LABELS.V_M}
                          </Checkbox>
                          <SpaceBetween direction={"horizontal"} size={"xxs"}>
                            <Box>{COMMON_LABELS.LEFT_PARENTHESIS}</Box>
                            <Checkbox
                              checked={searchInput.smccFlg}
                              onChange={({ detail: { checked } }) =>
                                setSearchInput({
                                  ...searchInput,
                                  smccFlg: checked,
                                })
                              }
                              disabled={searchInput.acquirer !== "2"}
                            >
                              {LABELS.SMCC}
                            </Checkbox>
                          </SpaceBetween>
                          <Checkbox
                            checked={searchInput.tfFlg}
                            onChange={({ detail: { checked } }) =>
                              setSearchInput({
                                ...searchInput,
                                tfFlg: checked,
                              })
                            }
                            disabled={searchInput.acquirer !== "2"}
                          >
                            {LABELS.TF}
                          </Checkbox>
                          <SpaceBetween direction={"horizontal"} size={"xxs"}>
                            <Checkbox
                              checked={searchInput.saisonFlg}
                              onChange={({ detail: { checked } }) =>
                                setSearchInput({
                                  ...searchInput,
                                  saisonFlg: checked,
                                })
                              }
                              disabled={searchInput.acquirer !== "2"}
                            >
                              {LABELS.SAISON}
                            </Checkbox>
                            <Box>{COMMON_LABELS.RIGHT_PARENTHESIS}</Box>
                          </SpaceBetween>
                          <Checkbox
                            checked={searchInput.jcbFlg}
                            onChange={({ detail: { checked } }) =>
                              setSearchInput({
                                ...searchInput,
                                jcbFlg: checked,
                              })
                            }
                            disabled={searchInput.acquirer !== "2"}
                          >
                            {LABELS.JCB}
                          </Checkbox>
                          <Checkbox
                            checked={searchInput.amexFlg}
                            onChange={({ detail: { checked } }) =>
                              setSearchInput({
                                ...searchInput,
                                amexFlg: checked,
                              })
                            }
                            disabled={searchInput.acquirer !== "2"}
                          >
                            {LABELS.AMEX}
                          </Checkbox>
                        </SpaceBetween>
                      </SpaceBetween>
                    </FormField>
                  </SpaceBetween>
                </FormField>
              </ColumnLayout>
              <FormField label={LABELS.EXAMINATION_STATUS}>
                <SpaceBetween direction={"horizontal"} size={"l"}>
                  <Checkbox
                    checked={searchInput.creditBeforeExaminationStatusFlg}
                    onChange={({ detail: { checked } }) =>
                      setSearchInput({
                        ...searchInput,
                        creditBeforeExaminationStatusFlg: checked,
                      })
                    }
                    disabled={applicationDisableFlg}
                  >
                    {EXAMINATION_STATUS.BEFORE}
                  </Checkbox>
                  <Checkbox
                    checked={searchInput.creditUnderExaminationStatusFlg}
                    onChange={({ detail: { checked } }) =>
                      setSearchInput({
                        ...searchInput,
                        creditUnderExaminationStatusFlg: checked,
                      })
                    }
                    disabled={applicationDisableFlg}
                  >
                    {EXAMINATION_STATUS.APPLYING}
                  </Checkbox>
                  <Checkbox
                    checked={searchInput.creditCompletedExaminationStatusFlg}
                    onChange={({ detail: { checked } }) =>
                      setSearchInput({
                        ...searchInput,
                        creditCompletedExaminationStatusFlg: checked,
                      })
                    }
                    disabled={applicationDisableFlg}
                  >
                    {EXAMINATION_STATUS.COMPLETED}
                  </Checkbox>
                </SpaceBetween>
              </FormField>
              <FormField label={LABELS.CLASSIFICATION}>
                <SpaceBetween direction={"horizontal"} size={"l"}>
                  <Checkbox
                    checked={searchInput.normalExaminationFlg}
                    onChange={({ detail: { checked } }) =>
                      setSearchInput({
                        ...searchInput,
                        normalExaminationFlg: checked,
                      })
                    }
                    disabled={applicationDisableFlg}
                  >
                    {CLASSIFICATION.NORMAL_APPLICATION}
                  </Checkbox>
                  <Checkbox
                    checked={searchInput.tenantExaminationFlg}
                    onChange={({ detail: { checked } }) =>
                      setSearchInput({
                        ...searchInput,
                        tenantExaminationFlg: checked,
                      })
                    }
                    disabled={applicationDisableFlg}
                  >
                    {CLASSIFICATION.TENANT_EXAMINATION}
                  </Checkbox>
                </SpaceBetween>
              </FormField>
              <FormField label={LABELS.EXAMINATION_TYPE}>
                <SpaceBetween direction={"horizontal"} size={"l"}>
                  <Checkbox
                    checked={searchInput.examinationTypeNewExaminationFlg}
                    onChange={({ detail: { checked } }) =>
                      setSearchInput({
                        ...searchInput,
                        examinationTypeNewExaminationFlg: checked,
                      })
                    }
                    disabled={applicationDisableFlg}
                  >
                    {EXAMINATION_TYPE.NEW_EXAMINATION}
                  </Checkbox>
                  <Checkbox
                    checked={searchInput.examinationTypeChangeExaminationFlg}
                    onChange={({ detail: { checked } }) =>
                      setSearchInput({
                        ...searchInput,
                        examinationTypeChangeExaminationFlg: checked,
                      })
                    }
                    disabled={applicationDisableFlg}
                  >
                    {EXAMINATION_TYPE.CHANGE_EXAMINATION}
                  </Checkbox>
                  <Checkbox
                    checked={searchInput.examinationTypeCancellationFlg}
                    onChange={({ detail: { checked } }) =>
                      setSearchInput({
                        ...searchInput,
                        examinationTypeCancellationFlg: checked,
                      })
                    }
                    disabled={applicationDisableFlg}
                  >
                    {EXAMINATION_TYPE.CANCELLATION}
                  </Checkbox>
                </SpaceBetween>
              </FormField>
            </SpaceBetween>
          </SpaceBetween>
        </Container>

        {/* POS登録 */}
        <Container>
          <SpaceBetween direction={"vertical"} size={"l"}>
            <RadioGroup
              items={[
                {
                  value: "2",
                  label: LABELS.POS_REGISTRATION_CONDITION,
                },
              ]}
              onChange={changePosRegistrationCondition}
              value={searchInput.checkTargetCode.toString()}
            />
            <ColumnLayout columns={5}>
              <PeriodDatePicker
                label={LABELS.REGISTRATION_DATE}
                startDate={searchInput.posRegisterApplicationStartDate}
                disabled={posDisableFlg}
                startDateOnChange={({ detail: { value } }) =>
                  setSearchInput({
                    ...searchInput,
                    posRegisterApplicationStartDate: changeDate(
                      formatDate(value),
                      searchInput.posRegisterApplicationEndDate,
                      true
                    ),
                  })
                }
                endDate={searchInput.posRegisterApplicationEndDate}
                endDateOnChange={({ detail: { value } }) =>
                  setSearchInput({
                    ...searchInput,
                    posRegisterApplicationEndDate: changeDate(
                      searchInput.posRegisterApplicationStartDate,
                      formatDate(value),
                      false
                    ),
                  })
                }
              />
            </ColumnLayout>
            <FormField label={LABELS.ORDER_TARGET}>
              <SpaceBetween direction={"horizontal"} size={"l"}>
                <RadioGroup
                  items={[
                    {
                      value: "1",
                      label: ORDER_TARGET.CREDIT,
                      disabled: posDisableFlg,
                    },
                    {
                      value: "2",
                      label: ORDER_TARGET.GMO,
                      disabled: posDisableFlg,
                    },
                  ]}
                  onChange={({ detail: { value } }) =>
                    setSearchInput({
                      ...searchInput,
                      posAcquirer: value,
                    })
                  }
                  value={searchInput.posAcquirer}
                />
              </SpaceBetween>
            </FormField>
            <FormField label={LABELS.POS_REGISTRATION_CONDITION}>
              <SpaceBetween direction={"horizontal"} size={"l"}>
                <Checkbox
                  checked={searchInput.posBeforeApplicationFlg}
                  onChange={({ detail: { checked } }) =>
                    setSearchInput({
                      ...searchInput,
                      posBeforeApplicationFlg: checked,
                    })
                  }
                  disabled={posDisableFlg}
                >
                  {POS_REGISTRATION_CONDITION.BEFORE}
                </Checkbox>
                <Checkbox
                  checked={searchInput.posUnderApplicationFlg}
                  onChange={({ detail: { checked } }) =>
                    setSearchInput({
                      ...searchInput,
                      posUnderApplicationFlg: checked,
                    })
                  }
                  disabled={posDisableFlg}
                >
                  {POS_REGISTRATION_CONDITION.APPLYING}
                </Checkbox>
                <Checkbox
                  checked={searchInput.posCompletedApplicationFlg}
                  onChange={({ detail: { checked } }) =>
                    setSearchInput({
                      ...searchInput,
                      posCompletedApplicationFlg: checked,
                    })
                  }
                  disabled={posDisableFlg}
                >
                  {POS_REGISTRATION_CONDITION.COMPLETED}
                </Checkbox>
              </SpaceBetween>
            </FormField>
          </SpaceBetween>
        </Container>
        {/* ドコモ審査 */}
        <Container>
          <SpaceBetween direction={"vertical"} size={"l"}>
            <RadioGroup
              items={[
                {
                  value: "3",
                  label: LABELS.DOCOMO_EXAMINATION,
                },
              ]}
              onChange={changeDocomoRegistrationCondition}
              value={searchInput.checkTargetCode.toString()}
            />
            <ColumnLayout columns={5}>
              <PeriodDatePicker
                label={LABELS.APPLICATION_DATE}
                startDate={searchInput.docomoExaminationApplicationStartDate}
                disabled={docomoDisableFlg}
                startDateOnChange={({ detail: { value } }) =>
                  setSearchInput({
                    ...searchInput,
                    docomoExaminationApplicationStartDate: changeDate(
                      formatDate(value),
                      searchInput.docomoExaminationApplicationEndDate,
                      true
                    ),
                  })
                }
                endDate={searchInput.docomoExaminationApplicationEndDate}
                endDateOnChange={({ detail: { value } }) =>
                  setSearchInput({
                    ...searchInput,
                    docomoExaminationApplicationEndDate: changeDate(
                      searchInput.docomoExaminationApplicationStartDate,
                      formatDate(value),
                      false
                    ),
                  })
                }
              />
            </ColumnLayout>
            <FormField label={LABELS.EXAMINATION_STATUS}>
              <SpaceBetween direction={"horizontal"} size={"l"}>
                <Checkbox
                  checked={searchInput.docomoBeforeExaminationFlg}
                  onChange={({ detail: { checked } }) =>
                    setSearchInput({
                      ...searchInput,
                      docomoBeforeExaminationFlg: checked,
                    })
                  }
                  disabled={docomoDisableFlg}
                >
                  {EXAMINATION_STATUS.BEFORE}
                </Checkbox>
                <Checkbox
                  checked={searchInput.docomoUnderExaminationFlg}
                  onChange={({ detail: { checked } }) =>
                    setSearchInput({
                      ...searchInput,
                      docomoUnderExaminationFlg: checked,
                    })
                  }
                  disabled={docomoDisableFlg}
                >
                  {EXAMINATION_STATUS.APPLYING}
                </Checkbox>
                <Checkbox
                  checked={searchInput.docomoCompletedExaminationFlg}
                  onChange={({ detail: { checked } }) =>
                    setSearchInput({
                      ...searchInput,
                      docomoCompletedExaminationFlg: checked,
                    })
                  }
                  disabled={docomoDisableFlg}
                >
                  {EXAMINATION_STATUS.COMPLETED}
                </Checkbox>
              </SpaceBetween>
            </FormField>
            <FormField label={LABELS.CLASSIFICATION}>
              <SpaceBetween direction={"horizontal"} size={"l"}>
                <Checkbox
                  checked={searchInput.docomoNormalExaminationFlg}
                  onChange={({ detail: { checked } }) =>
                    setSearchInput({
                      ...searchInput,
                      docomoNormalExaminationFlg: checked,
                    })
                  }
                  disabled={docomoDisableFlg}
                >
                  {CLASSIFICATION.NORMAL_APPLICATION}
                </Checkbox>
                <Checkbox
                  checked={searchInput.docomoTenantExaminationFlg}
                  onChange={({ detail: { checked } }) =>
                    setSearchInput({
                      ...searchInput,
                      docomoTenantExaminationFlg: checked,
                    })
                  }
                  disabled={docomoDisableFlg}
                >
                  {CLASSIFICATION.TENANT_EXAMINATION}
                </Checkbox>
              </SpaceBetween>
            </FormField>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </Container>
  );
};
